import React, { useRef, useCallback } from "react";
import DateTime from "react-datetime";
import "react-datetime/css/react-datetime.css";
import "./InputDateTime.css";
import PropTypes from "prop-types";

const InputDateTime = ({
  label,
  name,
  isRequired,
  isHidden,
  placeholder,
  onChange,
  onBlur,
  onKeyPress,
  value,
  hasError,
  errorMessage,
  dateFormat,
  isValidDate,
  isInline = false,
  isDisabled,
  boldLabel,
  ...props
}) => {
  const containerRef = useRef(null);

  // Mencari ancestor dengan CSS transform/filter/perspective (containing block)
  // karena position:fixed di dalam containing block akan relatif terhadapnya,
  // bukan terhadap viewport (terjadi pada Bootstrap modal .modal-dialog).
  const findContainingBlockOffset = (element) => {
    let el = element?.parentElement;
    while (el && el !== document.body) {
      const style = window.getComputedStyle(el);
      if (
        style.transform !== "none" ||
        style.filter !== "none" ||
        style.perspective !== "none"
      ) {
        const r = el.getBoundingClientRect();
        return { left: r.left, top: r.top };
      }
      el = el.parentElement;
    }
    return { left: 0, top: 0 };
  };

  // Posisikan calendar picker secara fixed relatif terhadap viewport.
  // Jika ruang di bawah input cukup → tampilkan di bawah,
  // jika tidak → tampilkan di atas.
  const positionCalendar = useCallback(() => {
    if (!containerRef.current) return;
    // Gunakan rect dari elemen <input> agar jarak picker ke input field
    // selalu konsisten (tidak terpengaruh label di dalam container)
    const input = containerRef.current.querySelector("input");
    if (!input) return;
    const rect = input.getBoundingClientRect();
    const picker = containerRef.current.querySelector(".rdtPicker");
    if (!picker) return;

    const PICKER_HEIGHT = picker.getBoundingClientRect().height || 280;
    const PICKER_WIDTH = picker.getBoundingClientRect().width || 280;
    const GAP = 0;
    const BOTTOM_MARGIN = 4; // margin aman dari batas bawah viewport
    const RIGHT_MARGIN = 8;  // margin aman dari batas kanan viewport

    const { left: cbLeft, top: cbTop } = findContainingBlockOffset(picker);

    picker.style.setProperty("position", "fixed", "important");
    picker.style.setProperty("z-index", "99999", "important");
    picker.style.removeProperty("bottom");

    // Mode mobile (<= 768px): posisikan dialog tepat di tengah layar
    if (window.innerWidth <= 768) {
      const centerLeft = Math.max(8, (window.innerWidth - PICKER_WIDTH) / 2);
      const centerTop = Math.max(8, (window.innerHeight - PICKER_HEIGHT) / 2);
      picker.style.setProperty("left", `${centerLeft - cbLeft}px`, "important");
      picker.style.setProperty("top", `${centerTop - cbTop}px`, "important");
      return;
    }

    picker.style.removeProperty("top");

    // Hitung posisi left: pastikan picker tidak melewati batas kanan viewport
    let leftPos = rect.left - cbLeft;
    const maxLeft = window.innerWidth - PICKER_WIDTH - RIGHT_MARGIN;
    if (leftPos > maxLeft) {
      leftPos = maxLeft;
    }
    if (leftPos < RIGHT_MARGIN) {
      leftPos = RIGHT_MARGIN;
    }
    picker.style.setProperty("left", `${leftPos}px`, "important");

    // Jika ruang di bawah input cukup (termasuk margin aman), tampilkan di bawah;
    // jika tidak, tampilkan di atas.
    if (rect.bottom + PICKER_HEIGHT + GAP + BOTTOM_MARGIN <= window.innerHeight) {
      picker.style.setProperty("top", `${rect.bottom - cbTop + GAP}px`, "important");
    } else {
      picker.style.setProperty("top", `${rect.top - cbTop - PICKER_HEIGHT - GAP}px`, "important");
    }
  }, []);

  // Custom renderInput: sisipkan positioning setelah calendar dirender
  const renderInput = (inputProps, openCalendar, closeCalendar) => {
    const handleClick = (e) => {
      inputProps.onClick?.(e);
      // Tunggu react-datetime render calendar, lalu posisikan
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          positionCalendar();
        });
      });
    };

    return (
      <input
        {...inputProps}
        onClick={handleClick}
        style={{ width: "100%" }}
      />
    );
  };

  return (
    <div
      className={`form-group ${hasError ? "has-error" : ""}`}
      ref={containerRef}
      style={
        isHidden
          ? { display: "none" }
          : {
              paddingTop: 2,
              paddingBottom: 2,
              paddingLeft: 0,
              paddingRight: 0,
            }
      }
    >
      <label
        className={isInline ? "mr-2" : ""}
        style={{
          fontWeight: boldLabel ? "600" : "400",
        }}
      >
        {label} {isRequired ? <sup className="text-danger">*</sup> : ""}
      </label>
      <DateTime
        dateFormat={dateFormat}
        timeFormat={false}
        closeOnClickOutside={true}
        closeOnSelect={true}
        onChange={onChange}
        onBlur={onBlur}
        onKeyPress={onKeyPress}
        value={value}
        renderInput={renderInput}
        inputProps={{
          placeholder: placeholder,
          name: name,
          autoComplete: "off",
          disabled: isDisabled,
        }}
        {...props}
        isValidDate={isValidDate}
      />
      {hasError && <small className="text-danger">{errorMessage}</small>}
    </div>
  );
};

InputDateTime.propTypes = {
  label: PropTypes.string,
  name: PropTypes.string,
  isRequired: PropTypes.bool,
  isHidden: PropTypes.bool,
  placeholder: PropTypes.string,
  onChange: PropTypes.func,
  onBlur: PropTypes.func,
  onKeyPress: PropTypes.func,
  value: PropTypes.string,
  hasError: PropTypes.bool,
  errorMessage: PropTypes.string,
  dateFormat: PropTypes.string,
};

export default InputDateTime;
